import * as React from 'react';
import { cn } from '../lib/cn';

/**
 * Input — text input primitive.
 *
 * RTL: text alignment is `text-start` so RTL Arabic types right-to-left.
 *      Optional leading/trailing icons sit on `inline-start`/`inline-end`
 *      via logical insets.
 *
 * a11y:
 *   - Required aria-invalid when `error` is set
 *   - aria-describedby wires to FormField helper text / error
 *   - Focus ring via :focus-visible
 */

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  error?: boolean;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, startAdornment, endAdornment, error, type = 'text', ...props }, ref) => {
    const baseClasses = cn(
      'flex h-10 w-full rounded-md border bg-surface-elevated px-3 py-2',
      // 16px on mobile so iOS Safari does not auto-zoom on focus. max-sm:
      // (not `text-base sm:text-sm`) so the base stays literally `text-sm`
      // and consumer overrides like `text-xs` keep winning at desktop —
      // an `sm:` re-assert here would outrank them via variant ordering.
      'text-sm max-sm:text-base text-text-primary placeholder:text-text-muted',
      'transition-colors duration-fast ease-out',
      'focus-visible:outline-none focus-visible:border-border-focus',
      'disabled:cursor-not-allowed disabled:opacity-60 disabled:bg-sand-50',
      'file:border-0 file:bg-transparent file:text-sm file:font-medium',
      error
        ? 'border-error-500 focus-visible:border-error-500'
        : 'border-border-default focus-visible:ring-0',
    );

    if (startAdornment || endAdornment) {
      return (
        <div
          className={cn(
            'relative flex h-10 w-full items-center rounded-md border bg-surface-elevated',
            'transition-colors duration-fast ease-out',
            'focus-within:border-border-focus',
            error
              ? 'border-error-500 focus-within:border-error-500'
              : 'border-border-default',
            (props.disabled || props.readOnly) && 'opacity-60 bg-sand-50',
            className,
          )}
        >
          {startAdornment ? (
            <span className="ps-3 pe-1 inline-flex items-center text-text-muted">
              {startAdornment}
            </span>
          ) : null}
          <input
            ref={ref}
            type={type}
            aria-invalid={error || undefined}
            className={cn(
              // Same iOS zoom guard as the plain variant (max-sm so consumer
              // size overrides keep winning at desktop).
              'flex-1 bg-transparent py-2 text-sm max-sm:text-base text-text-primary placeholder:text-text-muted',
              'focus:outline-none disabled:cursor-not-allowed',
              startAdornment ? 'ps-1' : 'ps-3',
              endAdornment ? 'pe-1' : 'pe-3',
            )}
            {...props}
          />
          {endAdornment ? (
            <span className="pe-3 ps-1 inline-flex items-center text-text-muted">
              {endAdornment}
            </span>
          ) : null}
        </div>
      );
    }

    return (
      <input
        ref={ref}
        type={type}
        aria-invalid={error || undefined}
        className={cn(baseClasses, className)}
        {...props}
      />
    );
  },
);
Input.displayName = 'Input';
